import React, { useContext, useEffect } from 'react';
import {
    BreadcrumbGroup,
    Button,
    Container,
    Header,
    Link,
    SpaceBetween,
    Flashbar,
    TextContent,
    Select,
    Modal,
    Box,
    FormField,
    Input,
} from '@amzn/awsui-components-console';

import { generateJwtToken, authenticateByBot } from './apis';
import {
    generateManualTestingUrl,
    getStageName,
    generateManualTestingUrlForIOS,
    getCurrentAuthUserAlias,
    isValidInput,
    displayErrorContent,
} from '../lib/utils';
import { AUTHENTICATION_FAILURE, SERVER_ERROR } from '../lib/constants';
import InputWithValidation from '../api-auth/bot-accounts/common/InputWithValidation';
import { listAllBots } from '../api-auth/bot-accounts/apis';
import { RegionContext } from '../CustomContext';

export default function ManualTestingService() {
    const [errorMessage, setErrorMessage] = React.useState([]);
    const [accessLoading, setAccessLoading] = React.useState(false);
    const [botAccessLoading, setBotAccessLoading] = React.useState(false);
    const [botLoginVisible, setBotLoginVisible] = React.useState(false);
    const [alias, setAlias] = React.useState('');
    const [botUsername, setBotUsername] = React.useState('');
    const [botPassword, setBotPassword] = React.useState('');
    const [botPasswordError, setBotPasswordError] = React.useState('');
    const [usersStatus, setUsersStatus] = React.useState('pending');
    const [usersSelectedOption, setUsersSelectedOption] = React.useState({});
    const [usersOptions, setUsersOptions] = React.useState([]);
    const [stage, setStage] = React.useState('alpha');
    const { selectedRegion, setSelectedRegion } = useContext(RegionContext);

    /**
     * process on user selection change event
     * @param {*} selectedOption
     */
    const onUsersSelectionChange = (selectedOption) => {
        setUsersSelectedOption(selectedOption);
        if (selectedOption.value !== alias) {
            setBotUsername(selectedOption.value);
            setBotPassword('');
        }
    };

    /**
     * process access event
     */
    const onAccessClick = async () => {
        setAccessLoading(true);
        setErrorMessage([]);
        if (usersSelectedOption.value === alias) {
            // login as current user
            try {
                const response = await generateJwtToken();
                const token = response['response'];
                window.location.href = generateManualTestingUrl(token, selectedRegion.value);
            } catch (e) {
                console.error('login as current user failed with error', e);
                displayErrorContent(
                    SERVER_ERROR,
                    `Fail to generate jwt token for current user ${alias}`,
                    setErrorMessage
                );
            }
        } else {
            // login as bot account user
            setBotLoginVisible(true);
        }
        setAccessLoading(false);
    };

    /**
     * process bot access event
     */
    const onBotClick = async () => {
        setBotAccessLoading(true);
        try {
            if (!isValidInput(botPassword)) {
                setBotPasswordError('Password is required and should not be empty.');
                setBotAccessLoading(false);
            } else {
                const response = await authenticateByBot(botUsername, botPassword);
                const token = response['response'];
                setBotAccessLoading(false);
                window.location.href = generateManualTestingUrl(token, selectedRegion.value);
            }
        } catch (err) {
            setBotAccessLoading(false);
            setBotLoginVisible(false);
            const errResponse = err.response;
            if (errResponse && errResponse.data && errResponse.status == 400) {
                displayErrorContent(
                    AUTHENTICATION_FAILURE,
                    'Invalid bot account username or password',
                    setErrorMessage
                );
            } else {
                displayErrorContent(
                    SERVER_ERROR,
                    `Fail to login as bot account user ${botUsername}, try again later or contact our dev team @ds2-device-farm-dev for more details.`,
                    setErrorMessage
                );
            }
        }
    };
    /**
     * load users
     */
    const loadUsers = () => {
        (async function () {
            setUsersStatus('loading');
            try {
                var result = [];
                // loading current user
                const currentUser = await getCurrentAuthUserAlias();
                const currentUserOption = { label: currentUser, value: currentUser };
                result.push({
                    label: 'Default access',
                    options: [currentUserOption],
                });
                setUsersSelectedOption(currentUserOption);
                setAlias(currentUser);

                // loading bot account user
                var botOptions = [];
                const response = await listAllBots();
                const bots = response['response'];
                bots.forEach((bot) => {
                    if (isValidInput(bot.username)) {
                        botOptions.push({ label: bot.username, value: bot.username });
                    }
                });
                result.push({
                    label: 'Bot account users',
                    options: botOptions,
                });
                setUsersOptions(result);
                setUsersStatus('finished');
            } catch (e) {
                console.error('Fail to load user(s) with error', e);
                setUsersStatus('error');
            }
        })();
    };

    useEffect(() => {
        // initialize the component
        setStage(getStageName());
        loadUsers();
    }, []);
    return (
        <SpaceBetween size="s">
            <Flashbar items={errorMessage} />
            <BreadcrumbGroup
                items={[
                    { text: 'DS2 Device Farm', href: '#/' },
                    {
                        text: 'Device checkout',
                        href: '#/device-checkout/home',
                    },
                ]}
            />
            <Header
                variant="h1"
                description="Get started by choosing a device"
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Select
                            selectedOption={usersSelectedOption}
                            expandToViewport
                            loadingText="Loading users"
                            errorText="Error fetching users"
                            finishedText="End of all results"
                            empty="No user found"
                            filteringType="auto"
                            virtualScroll={true}
                            statusType={usersStatus}
                            options={usersOptions}
                            onChange={({ detail }) => onUsersSelectionChange(detail.selectedOption)}
                        />
                        <Button loading={accessLoading} onClick={onAccessClick} variant="primary">
                            Access Device Farm
                        </Button>
                    </SpaceBetween>
                }
            >
                Device checkout
            </Header>

            <Modal
                onDismiss={() => setBotLoginVisible(false)}
                visible={botLoginVisible}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button onClick={() => setBotLoginVisible(false)} variant="link">
                                Cancel
                            </Button>
                            <Button onClick={onBotClick} loading={botAccessLoading} variant="primary">
                                Request access
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
                header={`Access Device Farm as bot account: ${botUsername}`}
            >
                <SpaceBetween direction="vertical" size="s">
                    <FormField label="Bot account username">
                        <Input value={botUsername} disabled />
                    </FormField>
                    <InputWithValidation
                        label="Bot account password"
                        type="password"
                        description=""
                        errorText={botPasswordError}
                        placeholder="Enter bot account password"
                        value={botPassword}
                        setValue={setBotPassword}
                        setError={setBotPasswordError}
                    ></InputWithValidation>
                </SpaceBetween>
            </Modal>

            <Container header={<Header variant="h2">Top features</Header>}>
                <TextContent>
                    <ol>
                        <li>
                            Seamless and fast access to FOS Tablets, Fire TV, Multimodal (Headed Alexa), Headless and
                            Android devices from your desktop
                        </li>
                        <li>Ability to take screenshots and video snippets</li>
                        <li>Support for viewing and downloading real-time device logs</li>
                        <li>Ability to flash devices from KBITs or side-load APKs</li>
                        <li>Support for executing adb shell commands</li>
                        <li>Support for Alexa testing on FOS Tablets, Multimodal and Headless</li>
                    </ol>
                </TextContent>
            </Container>
            <Container header={<Header variant="h2">Coming soon...</Header>}>
                <TextContent>
                    <ol>
                        <li>Support for adding more device types like iOS and Kindles</li>
                        <li>
                            An ecosystem where your phone, Alexa and smart home devices are connected and communicate
                            with each other
                        </li>
                        <li>Pre-configured devices pointed to devo and pre-prod end points</li>
                    </ol>
                </TextContent>
            </Container>
            <Container header={<Header variant="h2">Tips</Header>}>
                <TextContent>
                    <ol>
                        <li>
                            If you get logged-out of the manual testing screen, please visit the main Device Farm page
                            to login again and check out a device
                        </li>
                        <li>
                            To find device code names, visit{' '}
                            <a href="https://w.amazon.com/bin/view/Main/ExecutiveDeviceProgram/DeviceInformation/">
                                link
                            </a>
                        </li>
                        <li>
                            If you cannot locate the device you need, please open a{' '}
                            <a href="https://issues.amazon.com/issues/create?template=7daefe8d-ce17-4724-ad68-6f601292d755">
                                SIM{' '}
                            </a>
                            here to request adding the missing device type
                        </li>
                        <li>For any other queries please email us at ds2-device-farm-dev@amazon.com</li>
                    </ol>
                </TextContent>
            </Container>
        </SpaceBetween>
    );
}
